import { useMemo, useState } from 'react';

import { Square, SquareCheckBig } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { YES_NO_BASIC_OPTIONS } from '@/constants';
import { useQualityProgressionContext } from '@/contexts/QualityContext';
import useCognitoUsers from '@/hooks/useCognitoUsers';
import { useConfirmModal } from '@/hooks/useConfirmModal';
import { useQualityCheckEffectiveness } from '@/hooks/useQualitiesAPI';
import { getUserInfo } from '@/lib/auth';
import { cn } from '@/lib/utils';

const QualityProgressionEffectivenessPanel = () => {
  const [checkedCompleted, setCheckedCompleted] = useState();
  const { users } = useCognitoUsers();

  const { form, setFormByKey } = useQualityProgressionContext();
  const {
    ecCompleted,
    ecJustification,
    effectivenessSubmitted,
    planCriteriaEffectiveness,
    planCriteriaQA,
    status,
  } = form;
  const isQa = getUserInfo()?.['cognito:groups']?.includes('QA');
  const effectivenessConfirmed = Boolean(ecCompleted);
  const effectivenessLocked = Boolean(
    effectivenessConfirmed || effectivenessSubmitted || status === 'CLOSED'
  );
  const canCheckEffectiveness = status === 'PENDING_EC';
  const { checkEffectivenessMutation, isLoading } =
    useQualityCheckEffectiveness();
  const canSubmitEffectiveness =
    isQa &&
    canCheckEffectiveness &&
    checkedCompleted !== undefined &&
    !effectivenessLocked &&
    (checkedCompleted === 0 ||
      (checkedCompleted === 1 &&
        Boolean(ecJustification?.trim()))) &&
    !isLoading;

  const userMap = useMemo(() =>
    users.reduce((acc, u) => { acc[u.sub] = u.name; return acc; }, {}),
  [users]);

  const { showConfirmModal } = useConfirmModal();

  const confirmSubmit = async () => {
    if (!isQa || !canCheckEffectiveness) return;

    await checkEffectivenessMutation(form.id, {
      completed: checkedCompleted === 1,
      justification: ecJustification,
    });
    setFormByKey('effectivenessSubmitted', true);
    if (checkedCompleted === 1) {
      setFormByKey('ecCompleted', true);
    }
    setFormByKey('status', 'CLOSED');
  };

  const handleSubmit = () => {
    showConfirmModal({
      title: 'Confirm Effectiveness Check Completion',
      description: `You are confirming that checks were performed and the action plans' effectiveness were ${checkedCompleted === 1 ? 'verified' : 'not verified'}. This action indicates that the Quality Event is ready to be Closed.`,
      onConfirm: confirmSubmit,
    });
  };

  return (
    <Card
      className={cn(
        'mt-6',
        !canCheckEffectiveness && 'opacity-50 pointer-events-none'
      )}
    >
      <CardHeader>
        <CardTitle>Follow Up on Effectiveness Check</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Effectiveness Check Criteria</TableHead>
                <TableHead className="w-56">
                  Assigned QA for Verification
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell>{planCriteriaEffectiveness}</TableCell>
                <TableCell>
                  {
                    userMap[planCriteriaQA] || planCriteriaQA
                  }
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Card>
        <Card>
          <CardHeader className="p-4 grow">
            <div className="flex gap-4 text-sm">
              <div className="grow content-center">Is EC completed?</div>
              <div className="basis-40">
                <Select
                  disabled={
                    !isQa ||
                    !canCheckEffectiveness ||
                    effectivenessLocked ||
                    isLoading
                  }
                  onChange={({ target }) =>
                    setCheckedCompleted(Number(target.value))
                  }
                  value={effectivenessConfirmed ? 1 : (effectivenessLocked ? 0 : checkedCompleted)}
                >
                  {!effectivenessConfirmed && !effectivenessLocked && checkedCompleted === undefined && (
                    <option value="">-</option>
                  )}
                  {YES_NO_BASIC_OPTIONS.map(({ label, value }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="p-4 grow">
            <div className="flex gap-4 text-sm">
              <div className="grow content-center">
                Describe Evidence / Justification
              </div>
            </div>
          </CardHeader>
          <CardContent className="pb-4 px-4">
            <Textarea
              autoResize
              className="font-mono resize-none"
              disabled={
                !isQa ||
                !canCheckEffectiveness ||
                effectivenessLocked ||
                isLoading
              }
              onChange={({ target }) =>
                setFormByKey('ecJustification', target.value)
              }
              placeholder="Enter evidence details..."
              value={ecJustification || ''}
            />
          </CardContent>
        </Card>
        {isQa && (
          <div className="text-right">
            <Button disabled={!canSubmitEffectiveness} onClick={handleSubmit}>
              {effectivenessConfirmed ? <SquareCheckBig /> : <Square />}
              {checkedCompleted === 0 ? 'Submit' : 'Effectiveness Check Completed'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default QualityProgressionEffectivenessPanel;
